import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import {
  listWebhookDeliveries,
  rotateWebhookSecret,
  type AgentTask,
  type WebhookRotateResponse,
} from './api';

// WebhookTaskPanel renders inline under a webhook-triggered task row.
// Shows the inbound URL, lets the operator rotate the bearer secret
// (cleartext returned exactly once), and surfaces the most recent
// deliveries so the operator can confirm the upstream system is
// firing the agent.
export function WebhookTaskPanel({
  orgId,
  agentId,
  task,
}: {
  orgId: string;
  agentId: string;
  task: AgentTask;
}) {
  const qc = useQueryClient();
  const [freshSecret, setFreshSecret] = useState<WebhookRotateResponse | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const deliveries = useQuery({
    queryKey: ['webhook-deliveries', orgId, agentId, task.id],
    queryFn: () => listWebhookDeliveries(orgId, agentId, task.id, 10),
    enabled: !!task.webhook_secret_set_at,
    refetchInterval: 10_000,
  });

  const rotate = useMutation({
    mutationFn: () => rotateWebhookSecret(orgId, agentId, task.id),
    onSuccess: (res) => {
      setFreshSecret(res);
      qc.invalidateQueries({ queryKey: ['agent-tasks', orgId, agentId] });
    },
  });

  // URL is also derivable client-side, but we prefer the one the server
  // returned during a rotation since it picks the right Host. As a
  // fallback (first load before rotation) we compose it from window.
  const webhookURL =
    freshSecret?.webhook_url ??
    `${window.location.protocol}//${window.location.hostname.replace('agents.', 'api.')}/v1/webhooks/${task.id}`;

  async function copy(label: string, text: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      /* ignore */
    }
  }

  return (
    <div className="mt-2 space-y-3 rounded-lg border border-line-soft bg-surface-soft/40 p-3">
      <div>
        <p className="font-mono text-[10px] uppercase tracking-eyebrow text-ink-tertiary">
          Inbound webhook URL
        </p>
        <div className="mt-1 flex items-center gap-2">
          <code className="block flex-1 truncate rounded border border-line-soft bg-surface-white px-2 py-1 font-mono text-xs text-ink-primary">
            {webhookURL}
          </code>
          <button
            type="button"
            onClick={() => copy('url', webhookURL)}
            className="rounded border border-line bg-surface-white px-2 py-1 font-mono text-[10px] text-ink-secondary hover:border-ink-secondary"
          >
            {copied === 'url' ? 'copied' : 'copy'}
          </button>
        </div>
        <p className="mt-1 text-[11px] text-ink-tertiary">
          Send <code className="font-mono text-[10px]">POST</code> with{' '}
          <code className="font-mono text-[10px]">Authorization: Bearer &lt;secret&gt;</code>. JSON
          body is forwarded to the agent (truncated at 8 KB). Add an{' '}
          <code className="font-mono text-[10px]">X-Idempotency-Key</code> header (or include{' '}
          <code className="font-mono text-[10px]">event_id</code> in the body) to deduplicate
          retries.
        </p>
      </div>

      <div>
        <p className="font-mono text-[10px] uppercase tracking-eyebrow text-ink-tertiary">
          Bearer secret
        </p>
        {freshSecret ? (
          <div className="mt-1 space-y-1">
            <div className="flex items-center gap-2">
              <code className="block flex-1 truncate rounded border border-amber-200 bg-amber-50 px-2 py-1 font-mono text-xs text-ink-primary">
                {freshSecret.secret}
              </code>
              <button
                type="button"
                onClick={() => copy('secret', freshSecret.secret)}
                className="rounded border border-line bg-surface-white px-2 py-1 font-mono text-[10px] text-ink-secondary hover:border-ink-secondary"
              >
                {copied === 'secret' ? 'copied' : 'copy'}
              </button>
            </div>
            <p className="text-[11px] text-amber-700">
              Shown once. Copy it now — Sigil only persists the SHA-256 hash.
            </p>
          </div>
        ) : task.webhook_secret_prefix ? (
          <div className="mt-1 flex items-center justify-between gap-2">
            <code className="rounded border border-line-soft bg-surface-white px-2 py-1 font-mono text-xs text-ink-secondary">
              {task.webhook_secret_prefix}…
            </code>
            <Button
              variant="secondary"
              type="button"
              onClick={() => rotate.mutate()}
              disabled={rotate.isPending}
            >
              {rotate.isPending ? 'Rotating…' : 'Rotate secret'}
            </Button>
          </div>
        ) : (
          <div className="mt-1 flex items-center justify-between gap-2">
            <p className="text-xs text-ink-tertiary">
              No secret yet. Generate one before sending the first webhook.
            </p>
            <Button type="button" onClick={() => rotate.mutate()} disabled={rotate.isPending}>
              {rotate.isPending ? 'Generating…' : 'Generate secret'}
            </Button>
          </div>
        )}
        {rotate.error && (
          <p className="mt-1 text-[11px] text-danger">
            {(rotate.error as Error).message || 'Could not rotate'}
          </p>
        )}
      </div>

      <div>
        <p className="font-mono text-[10px] uppercase tracking-eyebrow text-ink-tertiary">
          Recent deliveries
        </p>
        {!deliveries.data || deliveries.data.length === 0 ? (
          <p className="mt-1 text-xs text-ink-tertiary">No deliveries yet.</p>
        ) : (
          <ul className="mt-1 space-y-1">
            {deliveries.data.map((d) => (
              <li
                key={d.id}
                className="flex items-center justify-between gap-2 rounded border border-line-soft bg-surface-white px-2 py-1 font-mono text-[11px] text-ink-secondary"
              >
                <span className="truncate">{d.event_id}</span>
                <Link
                  to={`/agents/${agentId}/runs/${d.run_id}`}
                  className="shrink-0 text-bit2me hover:underline"
                >
                  open run ↗
                </Link>
                <span className="shrink-0 text-ink-tertiary">{timeAgo(d.received_at)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.round(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}
